import { useCallback, useEffect, useRef, useState } from 'react';
import { Loader2, MoreVertical, Trash2, X } from 'lucide-react';

import { PasPracticeDrawer } from '../pas/PasPracticeDrawer';
import { knowledgeService } from '../../services/knowledgeService';
import { PAS_ANCHOR_PRACTICE_SEQUENCE_ID } from '../../utils/pasAsset';
import type { PasPracticeLibraryItem } from '../../utils/pasPracticeLibrary';
import {
  clearTtqLadderPracticeMetadata,
  readTtqLadderPracticeItems,
  writeTtqLadderPracticeMetadata,
} from '../../utils/ttqLadderPractice';
import { resolveSubjectCode } from '../../utils/subjectConfig';

type TtqParticleLadderPracticeModalProps = {
  open: boolean;
  particleId: string | null;
  particleTitle?: string | null;
  subject?: string | null;
  onClose: () => void;
  onToast?: (message: string) => void;
};

export function TtqParticleLadderPracticeModal({
  open,
  particleId,
  particleTitle,
  subject,
  onClose,
  onToast,
}: TtqParticleLadderPracticeModalProps) {
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState<PasPracticeLibraryItem[]>([]);
  const [moreOpen, setMoreOpen] = useState(false);
  const [clearing, setClearing] = useState(false);
  const moreRef = useRef<HTMLDivElement>(null);
  const subjectCode = resolveSubjectCode(subject);
  const isMath = subjectCode === 'math';

  useEffect(() => {
    if (!open || !particleId || !isMath) {
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    void knowledgeService.getKnowledgeParticle(particleId)
      .then((particle) => {
        if (cancelled) return;
        setItems(readTtqLadderPracticeItems(particle.metadata as Record<string, unknown> | undefined));
      })
      .catch((error) => {
        if (cancelled) return;
        onToast?.(error instanceof Error ? error.message : '加载阶梯练习失败');
        setItems([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isMath, onToast, open, particleId]);

  useEffect(() => {
    if (!moreOpen) return undefined;
    const handlePointerDown = (event: MouseEvent) => {
      if (!moreRef.current?.contains(event.target as Node)) {
        setMoreOpen(false);
      }
    };
    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [moreOpen]);

  useEffect(() => {
    if (!open) {
      setMoreOpen(false);
    }
  }, [open]);

  const handlePracticeLibraryChange = useCallback(async (
    _sequenceId: string,
    nextItems: PasPracticeLibraryItem[],
  ) => {
    if (!particleId) return;
    setItems(nextItems);
    const particle = await knowledgeService.getKnowledgeParticle(particleId);
    const metadata = writeTtqLadderPracticeMetadata(
      particle.metadata as Record<string, unknown> | undefined,
      nextItems,
    );
    await knowledgeService.updateKnowledgeParticle(particleId, { metadata });
  }, [particleId]);

  const handleClearLadderPractice = useCallback(async () => {
    if (!particleId || clearing) return;
    if (items.length === 0) {
      onToast?.('当前没有阶梯练习可清除');
      return;
    }
    const ok = window.confirm(
      '将清除该颗粒上已绑定的阶梯练习题目库，确定继续？',
    );
    if (!ok) return;
    setClearing(true);
    try {
      const particle = await knowledgeService.getKnowledgeParticle(particleId);
      const metadata = clearTtqLadderPracticeMetadata(
        particle.metadata as Record<string, unknown> | undefined,
      );
      await knowledgeService.updateKnowledgeParticle(particleId, { metadata });
      setItems([]);
      onToast?.('已清除阶梯练习');
    } catch (error) {
      onToast?.(error instanceof Error ? error.message : '清除阶梯练习失败');
    } finally {
      setClearing(false);
      setMoreOpen(false);
    }
  }, [clearing, items.length, onToast, particleId]);

  if (!open || !particleId) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-stretch justify-center bg-black/40 p-3 sm:p-6">
      <div className="flex min-h-0 w-full max-w-5xl flex-col overflow-hidden rounded-2xl border border-border bg-surface shadow-xl">
        <div className="flex shrink-0 items-center justify-between border-b border-border px-4 py-3">
          <div className="min-w-0">
            <p className="text-sm font-semibold text-ink">阶梯练习</p>
            <p className="truncate text-xs text-muted">
              {particleTitle?.trim() || '知识颗粒'}
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-1">
            <div ref={moreRef} className="relative">
              <button
                type="button"
                className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-border text-muted hover:bg-black/[0.04] hover:text-ink disabled:opacity-50"
                aria-label="阶梯练习更多操作"
                aria-expanded={moreOpen}
                disabled={clearing}
                onClick={() => setMoreOpen((value) => !value)}
              >
                <MoreVertical size={16} />
              </button>
              {moreOpen ? (
                <div className="absolute right-0 top-[calc(100%+4px)] z-20 min-w-[11rem] overflow-hidden rounded-lg border border-border bg-surface py-1 shadow-lg">
                  <button
                    type="button"
                    className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-danger hover:bg-danger/5 disabled:opacity-50"
                    disabled={clearing}
                    onClick={() => { void handleClearLadderPractice(); }}
                  >
                    <Trash2 size={14} />
                    清除阶梯练习
                  </button>
                </div>
              ) : null}
            </div>
            <button
              type="button"
              className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-border text-muted hover:bg-black/[0.04] hover:text-ink"
              aria-label="关闭阶梯练习"
              onClick={onClose}
            >
              <X size={16} />
            </button>
          </div>
        </div>

        <div className="relative min-h-0 flex-1">
          {!isMath ? (
            <div className="flex h-full items-center justify-center px-6 text-sm text-muted">
              当前学科暂不支持阶梯练习，请使用数学品题颗粒。
            </div>
          ) : loading ? (
            <div className="flex h-full items-center justify-center">
              <Loader2 size={24} className="animate-spin text-primary" />
            </div>
          ) : (
            <PasPracticeDrawer
              key={particleId}
              open
              presentation="page"
              embedded
              regionId={PAS_ANCHOR_PRACTICE_SEQUENCE_ID}
              assetId={particleId}
              sourceKpId={particleId}
              subject={subjectCode}
              initialHatchPrompt=""
              initialPracticeItems={items}
              onClose={onClose}
              onPracticeLibraryChange={handlePracticeLibraryChange}
              onToast={onToast}
              autoEnumerateDrs
            />
          )}
        </div>
      </div>
    </div>
  );
}
